import React from "react";
import { Trash2, CheckCircle2, Mail, Phone, Wallet, Clock, Filter } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { Header, IconBtn } from "@/pages/admin/AdminBlog";

const statuses = ["new", "contacted", "qualified", "converted", "lost"];

const statusStyle = (s) => ({
  new: "bg-rose-500/10 text-rose-300 border-rose-500/20",
  contacted: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  qualified: "bg-sky-500/10 text-sky-300 border-sky-500/20",
  converted: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  lost: "bg-slate-500/10 text-slate-400 border-slate-500/20",
}[s] || "bg-white/5 text-slate-300 border-white/10");

export default function AdminLeads() {
  const [items, setItems] = React.useState([]);
  const [filter, setFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");

  const load = async () => setItems((await api.get("/admin/leads")).data);
  React.useEffect(() => { load(); }, []);

  const setStatus = async (id, status_value) => {
    await api.patch(`/admin/leads/${id}?status_value=${encodeURIComponent(status_value)}`);
    toast.success("Status updated");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = items.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return l.name.toLowerCase().includes(s) || l.email.toLowerCase().includes(s) || l.phone.includes(s);
    }
    return true;
  });

  return (
    <div data-testid="admin-leads">
      <Header title="Leads — Portfolio Review" />

      <div className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex items-center gap-2 bg-[#0B1221] border border-white/10 rounded-lg px-3 py-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="leads-filter"
            className="bg-transparent text-sm text-white outline-none"
          >
            <option value="all">All statuses</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <input
          type="text"
          placeholder="Search by name, email, phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="leads-search"
          className="bg-[#0B1221] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-sky-500 outline-none flex-1"
        />
        <div className="text-xs text-slate-400">{filtered.length} of {items.length} leads</div>
      </div>

      <div className="mt-6 space-y-4">
        {filtered.map((l) => (
          <div key={l.id} className="card-luxury p-6" data-testid={`lead-row-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-white font-medium">{l.name}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle(l.status)}`}>{l.status}</span>
                  <Badge className="bg-white/5 text-slate-300 border-white/10 hover:bg-white/10">{l.source}</Badge>
                  <span className="text-slate-500 text-xs">{new Date(l.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {l.email}</span>
                  <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {l.phone}</span>
                  {l.investment_range && <span className="flex items-center gap-1.5 text-[#D4AF37]"><Wallet className="w-3 h-3" /> {l.investment_range}</span>}
                  {l.preferred_time && <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {l.preferred_time}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <select
                  value={l.status}
                  onChange={(e) => setStatus(l.id, e.target.value)}
                  className="bg-[#0B1221] border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:border-sky-500 outline-none"
                  data-testid={`lead-status-${l.id}`}
                >
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="flex">
                  <IconBtn onClick={() => setStatus(l.id, "converted")} testId={`mark-converted-${l.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></IconBtn>
                  <IconBtn onClick={() => remove(l.id)} variant="danger" testId={`delete-lead-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-slate-500 text-sm text-center py-10">No leads match.</div>}
      </div>
    </div>
  );
}
